import { FC, useEffect, useState, useRef } from 'react'
import { useGameStore } from '@/store'

interface DeadlineWarningProps {
  onDismiss?: () => void
}

// Warning thresholds in days
const WARNING_THRESHOLDS = [30, 14, 7, 3, 1]

export const DeadlineWarning: FC<DeadlineWarningProps> = ({ onDismiss }) => {
  const { session } = useGameStore()
  const [activeWarning, setActiveWarning] = useState<number | null>(null)
  const [isDismissed, setIsDismissed] = useState(false)
  const shownWarningsRef = useRef<Set<number>>(new Set())

  const daysRemaining = session?.goal.daysRemaining || 0

  useEffect(() => {
    if (!session || session.status !== 'active') return

    // Find the current warning threshold
    const currentThreshold = WARNING_THRESHOLDS.find(t => daysRemaining <= t)

    if (currentThreshold && !shownWarningsRef.current.has(currentThreshold)) {
      // Show warning for this threshold
      shownWarningsRef.current.add(currentThreshold)
      setActiveWarning(currentThreshold)
      setIsDismissed(false)

      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        setIsDismissed(true)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [daysRemaining, session])

  const handleDismiss = () => {
    setIsDismissed(true)
    onDismiss?.()
  }

  if (!activeWarning || isDismissed || !session || session.status !== 'active') {
    return null
  }

  const getWarningConfig = (days: number) => {
    if (days <= 1) {
      return {
        bg: 'bg-game-loss/90',
        border: 'border-game-loss',
        text: 'text-white',
        icon: '🚨',
        title: 'FINAL DAY',
        message: 'This is your LAST DAY! Make it count or go home empty-handed.',
      }
    }
    if (days <= 3) {
      return {
        bg: 'bg-game-loss/80',
        border: 'border-game-loss',
        text: 'text-white',
        icon: '⚠️',
        title: 'CRITICAL',
        message: `Only ${daysRemaining} days left! Time to make big moves or accept defeat.`,
      }
    }
    if (days <= 7) {
      return {
        bg: 'bg-yellow-500/80',
        border: 'border-yellow-400',
        text: 'text-black',
        icon: '⏰',
        title: 'WARNING',
        message: `${daysRemaining} days remaining. The clock is ticking, degen.`,
      }
    }
    if (days <= 14) {
      return {
        bg: 'bg-yellow-500/60',
        border: 'border-yellow-400',
        text: 'text-black',
        icon: '📅',
        title: 'HEADS UP',
        message: `${daysRemaining} days left. Halfway there - time to evaluate your strategy.`,
      }
    }
    return {
      bg: 'bg-game-accent/60',
      border: 'border-game-accent',
      text: 'text-white',
      icon: '📊',
      title: 'REMINDER',
      message: `${daysRemaining} days remaining to hit your goal. Stay focused.`,
    }
  }

  const config = getWarningConfig(activeWarning)

  return (
    <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4 animate-slide-down`}>
      <div className={`${config.bg} ${config.border} border-2 p-3 shadow-lg`}>
        <div className="flex items-start gap-3">
          <span className="text-2xl flex-shrink-0">{config.icon}</span>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <span className={`font-pixel text-sm ${config.text}`}>
                {config.title}
              </span>
              <button
                onClick={handleDismiss}
                className={`font-pixel text-xs ${config.text} opacity-70 hover:opacity-100`}
              >
                ✕
              </button>
            </div>
            <p className={`font-retro text-sm ${config.text} mt-1`}>
              {config.message}
            </p>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="mt-2 h-1 bg-black/20 overflow-hidden">
          <div
            className={`h-full ${config.text === 'text-black' ? 'bg-black/30' : 'bg-white/30'} animate-shrink-width`}
            style={{ animationDuration: '5s' }}
          />
        </div>
      </div>
    </div>
  )
}

// Add these styles to your global CSS or tailwind config
// @keyframes slide-down {
//   from { transform: translate(-50%, -100%); opacity: 0; }
//   to { transform: translate(-50%, 0); opacity: 1; }
// }
// @keyframes shrink-width {
//   from { width: 100%; }
//   to { width: 0%; }
// }
// .animate-slide-down { animation: slide-down 0.3s ease-out; }
// .animate-shrink-width { animation: shrink-width linear forwards; }
